import { motion } from "framer-motion";
import { Wine, Utensils, ChevronRight, History, Lock, RotateCcw, Plus, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const sectorTotal = (sector, counts) => {
  let s = 0;
  const ids = new Set(sector.products.map((p) => p.id));
  const locs = sector.locations.map((l) => l.id);
  ids.forEach((pid) => locs.forEach((loc) => {
    const e = counts[`${pid}~${loc}`];
    if (e) s += Number(e.quantity) || 0;
  }));
  return +s.toFixed(2);
};

export const SectorScreen = ({ inventory, catalog, counts, username, onPick, onHistory, onClose, onReopen, onNew, onChangeUser }) => {
  const isClosed = inventory?.status === "closed";
  const cards = [
    { id: "bar", label: "Bar", Icon: Wine, grad: "from-amber-600/30 to-amber-500/5", ring: "hover:ring-amber-500/50" },
    { id: "cuisine", label: "Cuisine", Icon: Utensils, grad: "from-orange-600/30 to-orange-500/5", ring: "hover:ring-orange-500/50" },
  ];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 bg-[#0D0E12]/90 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center shrink-0">
              <Wine className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <h1 data-testid="header-brand-title" className="font-display text-sm font-extrabold leading-none truncate">
                {inventory.name}
              </h1>
              <span className="text-[10px] font-semibold" style={{ color: isClosed ? "#f87171" : "#34d399" }}>
                {isClosed ? "Clôturé · lecture seule" : "● En cours"}
              </span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" data-testid="user-session-badge" className="h-9 rounded-xl gap-2 px-3 bg-secondary">
                <span className="h-6 w-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">
                  {username.charAt(0).toUpperCase()}
                </span>
                <span className="text-xs font-semibold max-w-16 truncate">{username}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover border-border w-56">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Session · {username}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-history-btn" onClick={onHistory}>
                <History className="h-4 w-4 mr-2" /> Historique
              </DropdownMenuItem>
              {!isClosed ? (
                <DropdownMenuItem data-testid="close-inventory-btn" onClick={onClose}>
                  <Lock className="h-4 w-4 mr-2" /> Clôturer l'inventaire
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem data-testid="reopen-inventory-btn" onClick={onReopen}>
                  <RotateCcw className="h-4 w-4 mr-2" /> Ré-ouvrir
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="new-inventory-btn" onClick={onNew}>
                <Plus className="h-4 w-4 mr-2" /> Nouvel inventaire
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="change-user-btn" onClick={onChangeUser}>
                <LogOut className="h-4 w-4 mr-2" /> Changer d'utilisateur
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h2 className="font-display text-2xl font-black tracking-tight">Choisissez un secteur</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-6">Sélectionnez la zone à inventorier.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {cards.map((c, i) => {
            const sector = catalog.sectors[c.id];
            const tot = sectorTotal(sector, counts);
            return (
              <motion.button
                key={c.id}
                data-testid={`sector-card-${c.id}`}
                onClick={() => onPick(c.id)}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={`relative overflow-hidden text-left rounded-3xl border border-border bg-gradient-to-br ${c.grad} to-card p-6 ring-1 ring-transparent ${c.ring} transition-all active:scale-[0.98]`}
              >
                <div className="flex items-center justify-between">
                  <c.Icon className="h-9 w-9 text-foreground/90" />
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="mt-8">
                  <div className="font-display text-2xl font-extrabold">{c.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {sector.products.length} produits · {sector.locations.length} salles
                  </div>
                  <div className="mt-3 font-mono-num text-3xl font-extrabold text-primary">{tot}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">unités comptées</div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
